<template>
  <Main>
    <BackButton text="Back to Projects List" @clicked="resetState()" :route="$routes.projects()" />
    <v-container style="max-width: 90%">
      <v-row>
        <v-col>
          <h1>
            {{ StringManipulation.methods.clearAndCapitalize(project.name) }}
          </h1>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="auto">
          <IgnoreWeightsSelector
            :project-id="project.id"
            :ignored-weights-server="ignoredWeightsServer"
          ></IgnoreWeightsSelector>
        </v-col>
        <v-spacer />
        <v-col cols="auto">
          <v-tooltip :disabled="kpis.length > 0 && validForAnalysis(false)" location="top">
            <template v-slot:activator="{props}">
              <span v-bind="props">
                <v-btn
                    class="mr-2"
                    :prepend-icon="analysisButtonColor(false) === 'blue-darken-4' ? '' : 'fa fa-warning'"
                    :color="analysisButtonColor(false)"
                    flat
                    :disabled="kpis.length === 0"
                    @click="router.get($routes.project_kpi_analysis(project.id))"
                    text="SII SPI Analysis"
                />
              </span>
            </template>
            <div v-if="kpis.length < 0">
              No KPIs specified as of yet.
            </div>
            <div v-else>
              Analysis will likely fail to run. <br/>
              Please check the following weights: <br/>
              <div v-html="formattedTooltip"></div>
            </div>
          </v-tooltip>

          <v-tooltip text="No PSIs specified as of yet." :disabled="psis.length > 0" location="top">
            <template v-slot:activator="{props}">
              <span v-bind="props">
                <v-btn
                    :prepend-icon="analysisButtonColor(true) === 'blue-darken-4' ? '' : 'fa fa-times'"
                    :color="analysisButtonColor(true)"
                    :disabled="psis.length === 0"
                    flat
                    @click="router.get($routes.project_psi_analysis(project.id))"
                    text=" PPI Analysis"
                />
              </span>
            </template>
          </v-tooltip>

        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <v-card>
            <v-tabs v-model="tab" style="background: #0d47a1; color: white">
              <v-tab style="text-transform: none" value="one">KPIs</v-tab>
              <v-tab style="text-transform: none" value="two">PSIs</v-tab>
            </v-tabs>

            <v-card-text>
              <v-window v-model="tab">
                <v-window-item reverse-transition="fade-transition" :transition="false" value="one">
                  <v-row>
                    <v-col cols="4">
                      <div class="mt-2 ml-1">
                        <v-text-field
                            v-model="search"
                            density="compact"
                            label="Search..."
                            prepend-inner-icon="fa fa-magnifying-glass mr-3"
                            :rounded="true"
                            bg-color="#d8d8f4"
                            variant="solo-filled"
                            hide-details
                            single-line
                        >
                        </v-text-field>
                      </div>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        class="text-none"
                        style="margin-top:10px"
                        color="green-darken-2"
                        @click="router.visit($routes.project_kpis_overview(project.id))"
                      >
                        KPIs OVERVIEW
                      </v-btn>
                    </v-col>
                    <v-spacer/>
                    <v-col cols="auto">
                      <ExpandedLinkButton
                        :route="$routes.new_project_kpi(project.id)"
                        text="Add KPI"/>
                      <br>

                    </v-col>

                  </v-row>
                  <v-row>
                    <v-col>
                      <v-data-table
                        v-model:search="search"
                        :headers="tableFieldKpis"
                        :items="kpisWithState"
                        item-key="id"
                        :page="kpiPage"
                        :items-per-page="kpiItems === 'max' ? kpisWithState.length : kpiItems"
                        @update:items-per-page="value => kpiItems = value"
                        @update:page="value => kpiPage = value"
                        :hide-default-footer="kpisWithState.length <= 5"
                        :row-props="rowRule"
                        class="elevation-3"
                        v-model:sort-by="kpiSortBy"
                      >
                        <template v-slot:item.name="{ item }">
                          <b>{{ item.name }}</b>
                        </template>

                        <template v-slot:item.id="{ item }">
                          {{ item.id }}
                          <i v-if="!item.state" class="fa fa-asterisk text-red" ></i>
                        </template>

                        <template v-slot:item.aggregation_levels="{ item }">
                          <v-chip v-for="level in item.aggregation_levels.split(', ')"
                              size="small"
                              :color="chipAggregationLevelColors[level]"
                              variant="flat"
                              class="mr-1 my-1" >
                            {{ level }}
                          </v-chip>
                        </template>

                        <template v-slot:item.action="{ item }">
                          <div class="d-flex">

                            <!-- DETAILS KPI BUTTON -->
                            <DetailsActionButton
                                :route="$routes.project_kpi(project.id, item.id)"
                                :item="item" descriptive-word="KPI" />

                            <!-- EDIT KPI BUTTON -->
                            <EditActionButton
                              :route="$routes.edit_project_kpi(project.id, item.id)"
                              :item="item" descriptive-word="KPI" />

                            <!-- DELETE BUTTON -->
                            <DeleteActionButton
                              :item="item"
                              item-type="KPI"
                              :route="$routes.project_kpi(project.id, item.id)" />

                          </div>
                        </template>
                      </v-data-table>
                    </v-col>
                  </v-row>
                  <div v-if="kpis.find((kpi) => !kpi.state)" class="mt-5">
                    <span>
                        <i style="font-size: 12px">
                          * KPIs in red are in incomplete state and will not be
                          considered in the analysis.
                        </i>
                  </span>
                  </div>
                </v-window-item>
                <v-window-item transition="fade-transition" :reverse-transition="false" value="two">
                  <v-row>
                    <v-col cols="4">
                      <div class="mt-2 ml-1">
                        <v-text-field
                            v-model="searchPSI"
                            density="compact"
                            label="Search..."
                            prepend-inner-icon="fa fa-magnifying-glass mr-3"
                            :rounded="true"
                            bg-color="#d8d8f4"
                            variant="solo-filled"
                            hide-details
                            single-line
                        >
                        </v-text-field>
                      </div>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                          class="text-none"
                          style="margin-top:10px"
                          color="green-darken-2"
                          @click="router.get($routes.project_psis_overview(project.id))"
                      >
                        PSIs OVERVIEW
                      </v-btn>
                    </v-col>
                    <v-spacer/>
                    <v-col cols="auto">
                      <ExpandedLinkButton
                          :route="$routes.new_project_psi(project.id)"
                          text="Add PSI"/>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <v-data-table
                        v-model:search="searchPSI"
                        :headers="tableFieldsPsis"
                        :items="psis"
                        item-key="id"
                        :items-per-page="psiItems === 'max' ? psis.length : psiItems"
                        :page="psiPage"
                        @update:items-per-page="value => psiItems = value"
                        @update:page="value => psiPage = value"
                        :hide-default-footer="psis.length <= 5"
                        class="elevation-1"
                      >
                        <template v-slot:item.name="{ item }">
                          <b>{{ item.name }}</b>
                        </template>

                        <template v-slot:item.action="{ item }">
                          <div class="d-flex">
                            <!-- DETAILS PSI BUTTON -->
                            <DetailsActionButton
                                :route="$routes.project_psi(project.id, item.id)"
                                :item="item" descriptive-word="PSI" />

                            <!-- EDIT PSI BUTTON -->
                            <EditActionButton
                                :route="$routes.edit_project_psi(project.id, item.id)"
                                :item="item" descriptive-word="PSI" />

                            <!-- DELETE PSI BUTTON -->
                            <DeleteActionButton item-type="PSI" :item="item" :route="$routes.project_psi(project.id, item.id)" />
                          </div>
                        </template>
                      </v-data-table>
                    </v-col>
                  </v-row>
                </v-window-item>
              </v-window>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </Main>
</template>

<script setup>
import {computed, onMounted, ref, watch} from "vue";
import Main from "@/Layouts/Main.vue";
import StringManipulation from "../../Mixins/StringManipulation";
import IgnoreWeightsSelector from "./subcomponents/IgnoreWeightsSelector.vue";
import ExpandedLinkButton from "@/Shared/ExpandedLinkButton.vue";
import { router } from "@inertiajs/vue3";
import * as $routes from "@/packs/routes";
import DeleteActionButton from "@/Shared/DeleteActionButton.vue";
import EditActionButton from "@/Shared/EditActionButton.vue";
import DetailsActionButton from "@/Shared/DetailsActionButton.vue";
import BackButton from "@/Shared/BackButton.vue";

const props = defineProps({
  project: {
    type: Object,
    required: true,
  },
  kpis: {
    type: Array,
    required: true,
  },
  psis: {
    type: Array,
    required: true,
  },
  notice: {
    type: String,
    required: false,
  },
  noticeType: {
    type: String,
    required: false,
  },
  errors: {
    type: Array,
    required: false,
    default: [],
  },
  alert: {
    type: String,
    required: false,
  },
  ignoredWeightsServer: {
    type: Array,
    required: false,
    default: () => [],
  },
  validForSiiSpiAnalysis: {
    type: Boolean,
    required: false,
    default: false,
  }
});

const storageKey = `project-show-state-${props.project.id}`;
const urlParams = new URLSearchParams(window.location.search);

let initialTab = urlParams.get('tab');
let kpiInitialPage = urlParams.get('kpiPage');
let kpiInitialItems = urlParams.get('kpiItems');
let kpiInitialSortBy = urlParams.get('kpiSortBy');
let kpiInitialSearch = urlParams.get('kpiSearch');
let psiInitialPage = urlParams.get('psiPage');
let psiInitialItems = urlParams.get('psiItems');
let psiInitialSearch = urlParams.get('psiSearch');

const chipAggregationLevelColors = {
  "Building": "red-darken-4",
  "City": "yellow-darken-1",
  "PEB": "blue-darken-4",
  "PED": "green-darken-4",
  "Island": "purple-darken-1"
}

// 1. If URL has no params (e.g., navigated back from another page), check sessionStorage
if (!initialTab && !kpiInitialPage && !kpiInitialItems && !psiInitialPage && !psiInitialItems && !kpiInitialSortBy && !kpiInitialSearch && !psiInitialSearch) {
  const savedState = sessionStorage.getItem(storageKey);
  if (savedState) {
    try {
      const parsed = JSON.parse(savedState);
      initialTab = parsed.tab;
      kpiInitialPage = parsed.kpiPage;
      kpiInitialItems = parsed.kpiItems;
      kpiInitialSortBy = parsed.kpiSortBy ? JSON.stringify(parsed.kpiSortBy) : null;
      kpiInitialSearch = parsed.kpiSearch;
      psiInitialPage = parsed.psiPage;
      psiInitialItems = parsed.psiItems;
      psiInitialSearch = parsed.psiSearch;
    } catch (e) {
      // Handle any JSON parsing errors silently
    }
  }
}

const resetState = () => {
  // Removing the state from the storage, makes all of them reset to their default values.
  sessionStorage.removeItem(storageKey);
}

const tab = ref(initialTab || 'one');
const kpiPage = ref(Number(kpiInitialPage) || 1);
const kpiItems = ref(kpiInitialItems === 'max' ? 'max' : (Number(kpiInitialItems) || 5));
const kpiSortBy = ref(kpiInitialSortBy ? JSON.parse(kpiInitialSortBy) : []);
const search = ref(kpiInitialSearch || "");
const psiPage = ref(Number(psiInitialPage) || 1);
const psiItems = ref(psiInitialItems === 'max' ? 'max' : (Number(psiInitialItems) || 5));
const searchPSI = ref(psiInitialSearch || "");


// 2. Watch for changes, update the URL, and save to sessionStorage purely on the frontend
watch(
    [tab, kpiPage, kpiItems, psiPage, psiItems, kpiSortBy, search, searchPSI],
    ([newTab, newKpiPage, newKpiItems, newPsiPage, newPsiItems, newKpiSortBy, newKpiSearch, newPsiSearch]) => {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', newTab);
      url.searchParams.set('kpiPage', newKpiPage);
      url.searchParams.set('kpiItems', newKpiItems);
      if (newKpiSortBy.length > 0) {
        url.searchParams.set('kpiSortBy', JSON.stringify(newKpiSortBy));
      } else {
        url.searchParams.delete('kpiSortBy');
      }
      if (newKpiSearch) {
        url.searchParams.set('kpiSearch', newKpiSearch);
      } else {
        url.searchParams.delete('kpiSearch');
      }
      url.searchParams.set('psiPage', newPsiPage);
      url.searchParams.set('psiItems', newPsiItems);
      if (newPsiSearch) {
        url.searchParams.set('psiSearch', newPsiSearch);
      } else {
        url.searchParams.delete('psiSearch');
      }

      // Updates the URL without triggering an Inertia backend request or page reload
      window.history.replaceState({}, '', url);

      // Save state so it survives navigation to other pages and back
      sessionStorage.setItem(storageKey, JSON.stringify({
        tab: newTab,
        kpiPage: newKpiPage,
        kpiItems: newKpiItems,
        kpiSortBy: newKpiSortBy,
        kpiSearch: newKpiSearch,
        psiPage: newPsiPage,
        psiItems: newPsiItems,
        psiSearch: newPsiSearch
      }));
    },
    { immediate: true, deep: true } // ensures URL & storage sync immediately on mount if restored from storage
)

// 3. Listen for browser Back/Forward buttons and update the Vue state accordingly
onMounted(() => {
  window.addEventListener('popstate', () => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('tab')) tab.value = params.get('tab');
    
    if (params.has('kpiPage')) kpiPage.value = Number(params.get('kpiPage'));
    if (params.has('kpiItems')) {
      const kpiItemsVal = params.get('kpiItems');
      kpiItems.value = kpiItemsVal === 'max' ? 'max' : Number(kpiItemsVal);
    }
    if (params.has('kpiSortBy')) {
      try {
        kpiSortBy.value = JSON.parse(params.get('kpiSortBy'));
      } catch (e) {
        kpiSortBy.value = [];
      }
    } else {
      kpiSortBy.value = [];
    }
    search.value = params.get('kpiSearch') || '';

    if (params.has('psiPage')) psiPage.value = Number(params.get('psiPage'));
    if (params.has('psiItems')) {
      const psiItemsVal = params.get('psiItems');
      psiItems.value = psiItemsVal === 'max' ? 'max' : Number(psiItemsVal);
    }
    searchPSI.value = params.get('psiSearch') || '';
  });
});

const tableFieldKpis = [
  { key: "id", title: "KPI ID" },
  { key: "name", title: "Name" },
  { key: "aggregation_levels", title: "Aggregation Levels" },
  { key: "dimension", title: "Dimension" },
  { key: "sectors", title: "Sectors" },
  { key: "action", title: "Actions", align: "center", sortable: false },
];

const tableFieldsPsis = [
  { key: "id", title: "PSI ID" },
  { key: "name", title: "Name" },
  { key: "call_impact", title: "Call Impact" },
  { key: "action", title: "Actions", align: "center" },
];

const kpisWithState = computed(() => {
  return props.kpis.map((kpi) => {
    return {
      ...kpi,
      _rowVariant: kpi.state ? undefined : "danger",
      name: StringManipulation.methods.clearAndCapitalize(kpi.name),
      aggregation_levels: kpi.aggregation_levels.join(", "), // Convert array to string
    };
  });
});

const formattedTooltip = computed(() => {
  return Object.entries(props.validForSiiSpiAnalysis ?? {})
      .map(([key, value]) => `${key}: ${StringManipulation.methods.clearAndCapitalize(value.join(', '))}`)
      .join('<br/>'); // Join with the HTML break tag
});

const validForAnalysis = (isPsi = false) => {
  return isPsi ? true : props.validForSiiSpiAnalysis === null;
}

const analysisButtonColor = (isPsi = false) => {
  if ((!isPsi && kpisWithState.value.length === 0) || (isPsi && props.psis.length === 0)) {
    return 'blue-darken-4';
  }

  return validForAnalysis(isPsi) ? 'blue-darken-4' : 'red-darken-4'
}

function rowRule(item) {
  if (!item.item.state) {
    return { class: 'incomplete-state' }
  }
}

</script>

<style>

thead {
  background: linear-gradient(to right, #e5f0ee, #b6c8e5);
}

th, td {
  font-size: 1rem;
}

.incomplete-state {
  background: linear-gradient(to right, #fff0f0, #ffe1e1, #fff0f0);

}

</style>